import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User,
  onAuthStateChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAnon: boolean;
  anonSessionId: string | null;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  continueAsGuest: () => void;
  getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAnon, setIsAnon] = useState(false);
  const [anonSessionId, setAnonSessionId] = useState<string | null>(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      setLoading(false);
      // upsert user profile on every auth state change (returning users too)
      if (u) {
        try {
          const token = await u.getIdToken();
          const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
          await fetch(`${baseUrl}/auth/verify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id_token: token }),
          });
        } catch (e) {
          // non-critical — user can still use the app
        }
      }
    });
    // restore anon session
    const stored = localStorage.getItem('nexel_anon_session');
    if (stored) {
      setAnonSessionId(stored);
      setIsAnon(true);
    }
    return unsub;
  }, []);

  const signInWithGoogle = async () => {
    // capture anon session before sign-in for migration
    const prevAnonId = localStorage.getItem('nexel_anon_session');
    const result = await signInWithPopup(auth, googleProvider);

    // verify token with backend — this upserts user record with name/email/picture
    if (result.user) {
      try {
        const token = await result.user.getIdToken();
        const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        await fetch(`${baseUrl}/auth/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id_token: token }),
        });
      } catch (e) {
        console.warn('Token verify failed (non-critical):', e);
      }
    }

    // migrate anon session data to the new authenticated user
    if (prevAnonId && result.user) {
      try {
        const token = await result.user.getIdToken();
        const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        await fetch(`${baseUrl}/auth/migrate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ anon_session_id: prevAnonId }),
        });
      } catch (e) {
        console.warn('Session migration failed (non-critical):', e);
      }
    }

    setIsAnon(false);
    setAnonSessionId(null);
    localStorage.removeItem('nexel_anon_session');
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
    setIsAnon(false);
    setAnonSessionId(null);
    localStorage.removeItem('nexel_anon_session');
  };

  const continueAsGuest = () => {
    const sid = `anon_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem('nexel_anon_session', sid);
    setAnonSessionId(sid);
    setIsAnon(true);
  };

  const getToken = async (): Promise<string | null> => {
    if (user) return user.getIdToken();
    return null;
  };

  return (
    <AuthContext.Provider value={{ user, loading, isAnon, anonSessionId, signInWithGoogle, signOut, continueAsGuest, getToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
